// GA4 conversion events for the pages the desktop app hands off to the browser:
// account activation (email link) and the Stripe success redirect (/upgraded).
// Both open in the user's default browser, so the _ga cookie from an earlier ad
// click is usually still there and GA4 / Google Ads can attribute the event.
//
// See docs/attribution-plan.md for how this fits the ad → trial → paid design.

import { getAttribution } from './attribution';

// Annual list prices (USD) — keep in sync with PricingTable.vue / ProRallyCard.vue.
export const PLAN_VALUES = {
  premium: 49,
  pro_rally: 199,
};

function onceKey(name, id) {
  return `rn_evt_${name}_${id || 'session'}`;
}

// True the first time it's called for this key in this tab session, so a
// refresh of the success page doesn't count a second conversion.
function firstTime(key) {
  try {
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, '1');
  } catch (e) {
    /* storage unavailable — fall through and send */
  }
  return true;
}

function send(name, params) {
  if (typeof window === 'undefined' || !window.gtag) return;
  const touch = getAttribution()?.lastTouch;
  window.gtag('event', name, {
    ...params,
    ...(touch?.utm_campaign && { campaign: touch.utm_campaign }),
  });
}

/** Trial account activated (the email-verification link succeeded). */
export function trackSignUp() {
  if (!firstTime(onceKey('sign_up'))) return;
  send('sign_up', { method: 'email' });
}

const SAMPLE_FILE_RE = /\.(rn2|gpx|kml|kmz|pdf|xlsx)$/i;

/**
 * Click handler for a guide page's sample-files section (event delegation):
 * sends `sample_download` for clicks on sample files, with the file name/type,
 * the page, and where on the page it was clicked (preview image, the
 * "download the sample" step, or the file list). Other links are ignored.
 */
export function trackSampleDownload(event) {
  const link = event.target && event.target.closest && event.target.closest('a[href]');
  if (!link) return;
  const path = decodeURIComponent(new URL(link.href, window.location.href).pathname);
  if (!SAMPLE_FILE_RE.test(path)) return;
  const fileName = path.split('/').pop();
  const sourceEl = link.closest('[data-sample-source]');
  send('sample_download', {
    file_name: fileName,
    file_type: fileName.split('.').pop().toLowerCase(),
    page_path: window.location.pathname,
    link_location: sourceEl ? sourceEl.getAttribute('data-sample-source') : 'list',
  });
}

/**
 * Paid subscription completed. Reads the plan and Stripe checkout session id
 * from the success URL when present (e.g. /upgraded?plan=pro_rally&session_id=cs_…);
 * unknown plans are valued at the Premium price so Ads still gets a (conservative) value.
 */
export function trackPurchase({ plan, transactionId } = {}) {
  if (!firstTime(onceKey('purchase', transactionId))) return;
  const known = Object.prototype.hasOwnProperty.call(PLAN_VALUES, plan);
  const value = known ? PLAN_VALUES[plan] : PLAN_VALUES.premium;
  send('purchase', {
    currency: 'USD',
    value,
    ...(transactionId && { transaction_id: transactionId }),
    items: [
      {
        item_id: known ? plan : 'unknown',
        item_name: known ? (plan === 'pro_rally' ? 'Pro Rally' : 'Premium') : 'Subscription',
        price: value,
        quantity: 1,
      },
    ],
  });
}
